import { cn } from '@/lib/utils'
import { hasWeekday, toggleWeekday, WEEKDAY_LONG, WEEKDAY_SHORT } from '../lib/rule'

const PRESETS = [
  { label: 'Every day', mask: 127 },
  { label: 'Weekdays', mask: 31 },
  { label: 'Weekends', mask: 96 },
] as const

/** Monday-first weekday toggles over the ISO bitmask (bit 0 = Monday). */
export function WeekdayToggles({ value, onChange, id, disabled, ...aria }: { value: number; onChange: (mask: number) => void; id?: string; disabled?: boolean; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div id={id} role="group" aria-label="Weekdays" aria-describedby={aria['aria-describedby']} className="flex gap-1">
        {WEEKDAY_SHORT.map((label, i) => {
          const on = hasWeekday(value, i)
          return (
            <button
              key={label}
              type="button"
              aria-pressed={on}
              aria-label={WEEKDAY_LONG[i]}
              disabled={disabled}
              onClick={() => onChange(toggleWeekday(value, i))}
              className={cn(
                'h-8 w-10 rounded-md border text-[12.5px] font-medium transition outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50',
                on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted',
                aria['aria-invalid'] && !on && 'border-destructive/60',
              )}
            >
              {label}
            </button>
          )
        })}
      </div>
      <div className="flex gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            disabled={disabled}
            onClick={() => onChange(p.mask)}
            className={cn('rounded-md px-2 py-1 text-[12px] text-muted-foreground hover:bg-muted hover:text-foreground', value === p.mask && 'text-primary')}
          >
            {p.label}
          </button>
        ))}
      </div>
    </div>
  )
}

/** 1–31 toggle grid for `monthly_dom`. */
export function DaysOfMonthPicker({ value, onChange, id, disabled, ...aria }: { value: number[]; onChange: (days: number[]) => void; id?: string; disabled?: boolean; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) {
  const set = new Set(value)
  return (
    <div id={id} role="group" aria-label="Days of the month" aria-describedby={aria['aria-describedby']} className="grid w-fit grid-cols-7 gap-1">
      {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => {
        const on = set.has(d)
        return (
          <button
            key={d}
            type="button"
            aria-pressed={on}
            aria-label={`Day ${d}`}
            disabled={disabled}
            onClick={() => onChange(on ? value.filter((x) => x !== d) : [...value, d].sort((a, b) => a - b))}
            className={cn(
              'size-8 rounded-md border text-[12.5px] font-medium tabular transition outline-none focus-visible:ring-[3px] focus-visible:ring-ring disabled:opacity-50',
              on ? 'border-primary bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:bg-muted',
              d >= 29 && !on && 'border-dashed',
            )}
          >
            {d}
          </button>
        )
      })}
    </div>
  )
}
